<template>
  <v-navigation-drawer color="grey-lighten-4" v-model="useMenuStore().isVisible" app>
    <template v-slot:prepend>
      <v-spacer />
      <div class="account-selector-wrapper">
        <change-stripe-account-selector @updateInitials="handleInitialsUpdate" />
      </div>
      <v-row class="justify-center my-3">
        <v-divider length="12rem" />
      </v-row>
    </template>
    <!-- Menu Options -->
    <template v-slot:default>
      <v-list class="text-grey-darken-1 text-title-1 pa-0" v-for="item in menu" :key="item.title">
        <v-list-item
          v-if="!item.subgroup"
          v-hasRoleAccessToRoute="{ pathName: item.to }"
          @click="!item.disabled && item.to ? onMenuClick(item) : ''"
          :active="isMenuItemActive(item)"
          :title="$t(`platform.menu.${item.title}`)"
        >
          <template v-if="item.disabled" v-slot:prepend>
            <v-tooltip :text="$t('toolTipsMessage.restricted_feature_message')">
              <template v-slot:activator="{ props }">
                <v-icon :color="item.iconColor" v-bind="props" :icon="item.icon" />
              </template>
            </v-tooltip>
          </template>
          <template v-else v-slot:prepend>
            <v-icon :icon="item.icon" />
          </template>
        </v-list-item>
        <v-list-group v-else :value="item.title">
          <template v-slot:activator="{ props }">
            <v-list-item
              v-bind="props"
              :active="isMenuItemActive(item)"
              :title="$t(`platform.menu.${item.title}`)"
            >
              <template v-slot:prepend>
                <v-icon>{{ item.icon }}</v-icon>
              </template>
            </v-list-item>
          </template>
          <v-list-item
            v-for="subgroupItem in item.subgroup"
            :key="subgroupItem.title"
            v-hasRoleAccessToRoute="{ pathName: subgroupItem.to }"
            @click="!subgroupItem.disabled ? onMenuClick(subgroupItem) : ''"
            :active="isMenuItemActive(subgroupItem)"
            :title="$t(`platform.menu.${subgroupItem.title}`)"
            style="padding-left: 30px !important"
          >
            <template v-if="subgroupItem.disabled" v-slot:prepend>
              <v-tooltip :text="$t('toolTipsMessage.restricted_feature_message')">
                <template v-slot:activator="{ props }">
                  <v-icon
                    :color="subgroupItem.iconColor"
                    v-bind="props"
                    :icon="subgroupItem.icon"
                  />
                </template>
              </v-tooltip>
            </template>
            <template v-else v-slot:prepend>
              <v-icon :color="subgroupItem.iconColor">{{ subgroupItem.icon }}</v-icon>
            </template>
          </v-list-item>
        </v-list-group>
      </v-list>
    </template>

    <!-- Navigation Drawer Footer -->
    <template v-slot:append>
      <v-list class="mb-16 text-grey-darken-1 text-title-1">
        <v-row class="justify-center my-3">
          <v-divider length="12rem" color="grey-darken-4 mb-3" />
        </v-row>
        <v-list-item
          link
          prepend-icon="mdi-headset"
          :title="$t('platform.menu.help')"
          @click="openProductlaneWidget"
        >
        </v-list-item>
        <v-list-item
          link
          @click="useAuthStore().logout()"
          prepend-icon="mdi-logout"
          :title="$t('platform.menu.logout')"
        >
        </v-list-item>
        <v-row class="mt-5" no-gutters>
          <v-col class="d-flex justify-center">
            <v-img
              src="@/shared/assets/NectarPay-withIcon.svg"
              max-height="150px"
              max-width="220px"
              min-height="80px"
            />
          </v-col>
        </v-row>
        <v-row no-gutters class="text-end">
          <v-col class="d-flex justify-center">
            <v-card-subtitle>{{ appVersion }}</v-card-subtitle>
          </v-col>
        </v-row>
      </v-list>
    </template>
  </v-navigation-drawer>
</template>

<script lang="ts" setup>
import { ref, computed, watchEffect } from "vue";
import { useAccountStore } from "@/account/stores/account";
import { useMenuStore } from "@/shared/stores/global";
import { useAuthStore } from "@/auth/stores/auth";
import { PlatformFeature } from "@/shared/enums/feature.enum";
import { AccountPlan } from "@/shared/enums/status.enum";
import { getInitials } from "@/shared/utils/initials.utils";
import ChangeStripeAccountSelector from "@/shared/components/ChangeStripeAccountSelector.vue";
import { useRoute, useRouter } from "vue-router";
import { UserRoleStatus } from "@/shared/enums/status.enum";
import { hasRoleAccessToAction } from "@/shared/utils/role.utils";
import { hasFeatureAccess } from "@/shared/utils/feature.utils";

// Define interfaces for menu items
interface SubgroupItem {
  title: string;
  icon: string;
  to: string;
  iconColor: string;
  disabled: boolean;
  activeRoutes?: string[];
  isOwnerOrAdmin?: boolean;
  isOwnerOnly?: boolean;
  feature?: PlatformFeature;
}

interface MenuItem {
  title: string;
  icon: string;
  to?: string;
  order: number;
  iconColor: string;
  disabled: boolean;
  activeRoutes?: string[];
  feature?: PlatformFeature;
  isOwnerOrAdmin?: boolean;
  subgroup?: SubgroupItem[];
}

declare global {
  interface Window {
    Productlane?: {
      open?: () => void;
    };
  }
}

const accountInitials = ref("");
const router = useRouter();
const route = useRoute();
const appVersion = ref(import.meta.env.APP_VERSION);
const accountPlan = ref("");

const handleInitialsUpdate = (accountName: string) => {
  accountInitials.value = getInitials(accountName);
};

watchEffect(() => {
  accountPlan.value = useAuthStore().accountPlan;
});

const allMenus: MenuItem[] = [
  {
    title: PlatformFeature.DASHBOARD,
    icon: "mdi-view-dashboard-outline",
    to: "dashboard",
    order: 1,
    iconColor: "",
    disabled: false,
  },
  {
    title: PlatformFeature.PAYMENTS,
    icon: "mdi-credit-card-multiple-outline",
    to: "payments",
    order: 2,
    iconColor: "",
    disabled: false,
  },
  {
    title: PlatformFeature.PRODUCT_CATALOG,
    icon: "mdi-cart-outline",
    order: 3,
    iconColor: "",
    disabled: false,
    subgroup: [
      {
        title: PlatformFeature.PRODUCTS,
        icon: "mdi-package-variant-closed",
        to: "products",
        iconColor: "",
        disabled: false,
        activeRoutes: ["products", "productDetails", "createProduct", "productUpdate"],
      },
      {
        title: "productClasses",
        icon: "mdi-shape-outline",
        to: "productClasses",
        iconColor: "",
        disabled: false,
        activeRoutes: [
          "productClasses",
          "productClassDetails",
          "createProductClass",
          "updateProductClass",
        ],
      },
      {
        title: "productTags",
        icon: "mdi-tag-multiple-outline",
        to: "productTags",
        iconColor: "",
        disabled: false,
        activeRoutes: ["productTags", "productTagDetails", "createProductTag", "updateProductTag"],
      },
    ],
  },
  {
    title: PlatformFeature.TERMINALS,
    icon: "mdi-cellphone-cog",
    order: 4,
    iconColor: "",
    disabled: false,
    isOwnerOrAdmin: true,
    subgroup: [
      {
        title: PlatformFeature.FLEET_MANAGEMENT,
        icon: "mdi-map-marker-multiple-outline",
        to: "locations",
        iconColor: "",
        disabled: false,
      },
      {
        title: PlatformFeature.FORMS,
        icon: "mdi-note-edit-outline",
        to: "terminalForms",
        iconColor: "",
        disabled: false,
      },
      {
        title: "hardwareOrders",
        icon: "mdi-package-variant-closed",
        to: "hardwareOrders",
        iconColor: "",
        disabled: false,
        activeRoutes: ["hardwareOrders", "hardwareShop"],
        isOwnerOnly: true,
      },
    ],
  },
  {
    title: PlatformFeature.PAYMENT_LINKS,
    icon: "mdi-link-variant",
    to: "paymentLinks",
    order: 5,
    iconColor: "",
    disabled: false,
  },
  {
    title: PlatformFeature.CUSTOMERS,
    icon: "mdi-account-group-outline",
    to: "customers",
    order: 6,
    iconColor: "",
    disabled: false,
  },
  {
    title: PlatformFeature.USERS,
    icon: "mdi-account-supervisor-circle-outline",
    to: "users",
    order: 7,
    iconColor: "",
    disabled: false,
    isOwnerOrAdmin: true,
  },
  {
    title: PlatformFeature.REPORTS,
    icon: "mdi-file-chart-outline",
    to: "reports",
    order: 8,
    iconColor: "",
    disabled: false,
  },
  {
    title: PlatformFeature.IMPORT_DATA,
    icon: "mdi-database-import-outline",
    to: "importData",
    order: 9,
    iconColor: "",
    disabled: false,
    isOwnerOrAdmin: true,
  },
  {
    title: PlatformFeature.ACCOUNT,
    icon: "mdi-bank",
    order: 10,
    iconColor: "",
    disabled: false,
    to: "account",
    isOwnerOrAdmin: true,
  },
];

// handle menu based on account plan
const menu = computed(() => {
  // First filter out items based on feature flags and user roles
  const featureFilteredMenu = allMenus
    .map((item) => {
      // Skip items that should only be shown to admins/owners
      if (item.isOwnerOrAdmin && useAuthStore().getRoleName === "staff") {
        return null;
      }

      // Handle subgroup items with feature flags
      if (item.subgroup) {
        const filteredSubgroup = item.subgroup
          .filter(
            (subgroupItem) =>
              (!subgroupItem.feature || hasFeatureAccess(subgroupItem.feature)) &&
              (!subgroupItem.isOwnerOrAdmin || useAuthStore().getRoleName !== "staff") &&
              (!subgroupItem.isOwnerOnly || useAuthStore().isOwner),
          )
          .map((subgroupItem) => subgroupItem);

        return {
          ...item,
          subgroup: filteredSubgroup,
        };
      }

      // Handle top-level items with feature flags
      if (item.feature && !hasFeatureAccess(item.feature)) {
        return null;
      }

      return item;
    })
    .filter((item): item is MenuItem => item !== null);

  // Then apply account plan restrictions
  if (accountPlan.value === AccountPlan.STARTER) {
    const restrictedMenu = featureFilteredMenu.map((item) => {
      if (
        item.to === "paymentLinks" ||
        item.to === "products" ||
        item.to === "customers" ||
        item.to === "importData"
      ) {
        return {
          ...item,
          disabled: true,
          icon: "mdi-lock-outline",
          iconColor: "light-blue",
        };
      }

      if (item.subgroup) {
        const restrictedSubgroup = item.subgroup.map((subgroupItem) => {
          if (
            subgroupItem.to === "terminalForms" ||
            subgroupItem.to === "products" ||
            subgroupItem.to === "productClasses" ||
            subgroupItem.to === "productTags"
          ) {
            return {
              ...subgroupItem,
              disabled: true,
              icon: "mdi-lock-outline",
              iconColor: "light-blue",
            };
          }
          return subgroupItem;
        });
        return {
          ...item,
          subgroup: restrictedSubgroup,
        };
      }

      return item;
    });
    return restrictedMenu;
  }
  return featureFilteredMenu;
});

const logoURL = computed(() => {
  return useAccountStore().account?.logoURL || "";
});

const openProductlaneWidget = () => {
  window.Productlane?.open?.();
};

const onMenuClick = (item: MenuItem | SubgroupItem) => {
  router.push({ name: `${item.to}` });
};

const routeStartsWith = (targetPath: string) => {
  const currentPath = route.path.replace(/\/$/, "");
  const normalizedTargetPath = targetPath.replace(/\/$/, "");

  return currentPath === normalizedTargetPath || currentPath.startsWith(`${normalizedTargetPath}/`);
};

const isMenuItemActive = (item: MenuItem | SubgroupItem) => {
  if ("subgroup" in item && item.subgroup) {
    return item.subgroup.some(isMenuItemActive);
  }

  if (item.activeRoutes?.includes(String(route.name))) {
    return true;
  }

  if (!item.to || !router.hasRoute(item.to)) {
    return false;
  }

  const targetRoute = router.resolve({ name: item.to });

  return (
    route.name === targetRoute.name ||
    (targetRoute.path !== "/" && routeStartsWith(targetRoute.path))
  );
};
</script>

<style scoped>
.account-selector-wrapper {
  padding: 10px 12px 2px;
}
</style>
